import { useMemo } from 'react';
import { useTranslation } from '@frontend/i18n';
import { useScopedAppFlagStore } from '@frontend/scope';
import { formatters } from '@frontend/shared';
import { featureFlags } from '../../../../feature-flags';
import { RoiSummary, TabCardProps } from '../../components';
import { roiDefinitions } from '../../helpers';
import { useIsACI } from '../../hooks';
import { useARRoiShallowStore } from '../hooks';
import { AROverviewData } from '../types';

interface ARSummaryProps {
  data: AROverviewData;
}

export const ARSummary = ({ data }: ARSummaryProps) => {
  const { t } = useTranslation('analytics');
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const isDemoAccount = getFeatureFlagValue(featureFlags.missedCallTextRoiDemoData);
  const { definitionsHelperDateRange } = useARRoiShallowStore('definitionsHelperDateRange');
  const isACILocation = useIsACI();

  const summaryCardsData: TabCardProps[] = useMemo(() => {
    return [
      {
        title: t('Appointment Reminders Sent'),
        data: [
          {
            label: t('{{count}} Patients', { count: data.remindersSent.patientsCount }),
            value: formatters.value(data.remindersSent.remindersCount),
            infoHoverText: t('{{messages}} Messages were sent to {{patients}} Patients', {
              messages: data.remindersSent.remindersCount,
              patients: data.remindersSent.patientsCount,
            }),
          },
        ],
      },
      {
        title: t('Appointments Confirmed'),
        data: [
          {
            label: t('{{count}} Patients', { count: data.appointmentsConfirmed.patientsCount }),
            value: formatters.value(data.appointmentsConfirmed.appointmentsCount),
            infoHoverText: t('A total of {{appointments}} appointments were confirmed by {{patients}} patients', {
              appointments: data.appointmentsConfirmed.appointmentsCount,
              patients: data.appointmentsConfirmed.patientsCount,
            }),
          },
        ],
      },
      {
        title: t('Production Value'),
        data: [
          {
            label: t('Appointments Completed'),
            value: formatters.value(data.appointmentsCompleted.appointmentsCompletedByReminders),
            infoHoverText: t('Total completed appointments {{dateRangeHelperText}}', {
              dateRangeHelperText: definitionsHelperDateRange,
            }),
          },
          {
            label: t('Approx Value'),
            value: formatters.shortenCurrency(data.appointmentsCompleted.production),
            infoHoverText: roiDefinitions.productionValueDefinition(definitionsHelperDateRange),
          },
        ],
        omit: isACILocation,
      },
    ];
  }, [data]);

  return <RoiSummary isDemoAccount={isDemoAccount} summaryCardsData={summaryCardsData} />;
};
